import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useCompany } from '../../../contexts/CompanyContext';
import { getUpcomingJobs } from '../../../services/contractorService';
import Header from '../../../components/ui/Header';
import Sidebar from '../../../components/ui/Sidebar';
import { ContractorJobHierarchy } from '../../../components/contractor/jobs';

/**
 * UpcomingJobs
 *
 * Displays all jobs assigned to the contractor for future dates
 * using the hierarchical Job Site → Job → Instance layout.
 * Note: Contact person is NOT shown on upcoming jobs (only on Today's Jobs).
 */
const UpcomingJobs = () => {
  const { profile } = useAuth();
  const { currentCompany } = useCompany();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();

  const highlightId = searchParams.get('highlight');

  // Fetch upcoming jobs
  const fetchJobs = useCallback(async () => {
    if (!profile?.id || !currentCompany?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const timezone = currentCompany?.timezone || 'Australia/Sydney';
      const data = await getUpcomingJobs(profile.id, currentCompany.id, timezone);
      setJobs(data);
    } catch (err) {
      console.error('Error loading upcoming jobs:', err);
      setError('Failed to load upcoming jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [profile?.id, currentCompany?.id, currentCompany?.timezone]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Header />

        <main className="flex-1 overflow-y-auto overflow-x-hidden pt-16 pl-0 lg:pl-60">
          <div className="max-w-full px-4 py-6 sm:px-6 sm:py-8 sm:container sm:mx-auto">
            {/* Page Header */}
            <div className="mb-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Upcoming Jobs</h1>
              <p className="text-text-secondary mt-2">Jobs scheduled for future dates</p>
              <div className="mt-4 text-lg">
                <span className="text-text-primary font-semibold">{jobs?.length || 0}</span>
                <span className="text-text-secondary"> Total Upcoming Jobs</span>
              </div>
            </div>

            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 mb-6">
                <p className="text-red-400">{error}</p>
              </div>
            )}

            {/* Job Hierarchy */}
            {!loading && !error && (
              <ContractorJobHierarchy
                instances={jobs}
                mode="upcoming"
                highlightInstanceId={highlightId}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default UpcomingJobs;
